/* eslint-disable @typescript-eslint/member-ordering */
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, Injector, inject } from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as Sentry from '@sentry/angular';
import { AbilityService } from 'ability';
import jwtDecode from 'jwt-decode';
import { RoutingPath } from 'mbs-ui-shared@constants';
import {
  AccountTypeEnum,
  Administrator,
  BrandingSetting,
  ExtendedOnlineAccessInfo,
  JwtToken,
  OAuthCredential,
  TempTokenData
} from 'mbs-ui-shared@models';
import { OAuthCodeParams } from 'mbs-ui-shared@models/OAuthCredential';
import TwoFactorComputers from 'mbs-ui-shared@models/TwoFactorComputers';
import { AuthCredentials, AuthGoogleCredentials, AuthIdCredentials, AuthMicrosoftCredentials } from 'mbs-ui-shared@models/auth-models';
import { DataForLoginUser } from 'mbs-ui-shared@models/login';
import { OneTimeAuthToken } from 'mbs-ui-shared@models/one-time-token.model';
import { PassCheckType, SetPasswordParams } from 'mbs-ui-shared@models/password';
import { AppPersistentStateService } from 'mbs-ui-shared@services/app-persistent-state.service';
import { AuthOnlineAccessService } from 'mbs-ui-shared@services/auth/auth-online-access.service';
import { AuthService } from 'mbs-ui-shared@services/auth/auth.service';
import { OneTimeSessionService } from 'mbs-ui-shared@services/auth/one-time-session.service';
import { PermissionsService } from 'mbs-ui-shared@services/auth/permissions.service';
import { EnvironmentService } from 'mbs-ui-shared@services/environments.service';
import { PasswordService } from 'mbs-ui-shared@services/password.service';
import {
  BehaviorSubject,
  Observable,
  Subject,
  catchError,
  combineLatest,
  filter,
  finalize,
  map,
  noop,
  of,
  share,
  switchMap,
  take,
  tap,
  throwError
} from 'rxjs';

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class AuthFacade {
  protected injector = inject(Injector);
  protected authService = inject(AuthService);
  protected abilityService = inject(AbilityService);
  protected appPersistent = inject(AppPersistentStateService);
  protected router = inject(Router);
  protected onlineAccessService = inject(AuthOnlineAccessService);
  protected oneTimeSession = inject(OneTimeSessionService);
  protected passwordService = inject(PasswordService);
  public authError: HttpErrorResponse;

  private meLoaded$ = new BehaviorSubject<boolean>(false);
  private meLoading$ = new BehaviorSubject<boolean>(false);
  protected tokenLoading$ = new BehaviorSubject<boolean>(false);

  private currentUserInternal$ = new BehaviorSubject<Administrator>(null);
  private currentBrandingSettingInternal$ = new BehaviorSubject<BrandingSetting>(null);
  protected oAuthCredentialInternal$ = new BehaviorSubject<OAuthCredential>(this.authService.getTokenData());
  private twoFAConfirmedInternal$ = new Subject<boolean>();

  // Lazy getter to break circular dependency:
  // something → EnvironmentService → [chain] → AuthFacade → EnvironmentService [CIRCULAR]
  // environment is only used inside initialStreams() tap(), so it's safe to resolve lazily.
  protected get environment(): EnvironmentService {
    return (this._environment ??= this.injector.get(EnvironmentService));
  }
  private _environment: EnvironmentService;

  constructor() {
    this.initialStreams();
  }

  public twoFAConfirmed = this.twoFAConfirmedInternal$.asObservable();
  public loaded$ = this.meLoaded$.asObservable(); // flag to indicate that the current user is logged in and the user data is loaded
  public loading$ = combineLatest([this.tokenLoading$.asObservable(), this.meLoading$.asObservable()]).pipe(
    map(([tokenLoading, meLoading]) => tokenLoading || meLoading)
  );

  // Online Access part

  public get isMBSMode(): boolean {
    return this.onlineAccessService.isMBSMode();
  }

  public get onlineAccessKey(): string {
    return this.onlineAccessService.getOnlineAccessKey();
  }

  public get onlineAccessInfo$(): Observable<ExtendedOnlineAccessInfo> {
    return this.onlineAccessService.onlineAccessInfo$();
  }

  // Getters part

  public get currentUser$(): Observable<Administrator> {
    return this.currentUserInternal$.asObservable();
  }

  public get authToken(): string {
    const tokenData = this.oAuthCredentialInternal$?.value ?? null;

    return tokenData ? `${tokenData.token_type} ${tokenData.access_token}` : '';
  }

  public get requestAuthToken(): string {
    return this.isOneTimeSession ? this.oneTimeSession.authToken : this.authToken;
  }

  public get isOneTimeSession(): boolean {
    return this.oneTimeSession.isActive;
  }

  public get refreshToken(): OAuthCredential {
    return this.oAuthCredentialInternal$.getValue();
  }

  public get fetchCurrentUser$(): Observable<Administrator> {
    return this.fetchUser$();
  }

  public get accessToken(): string {
    return this.oAuthCredentialInternal$?.value?.access_token;
  }

  public get decodedToken(): JwtToken {
    return new JwtToken(jwtDecode(this.accessToken));
  }

  public get currentUserValue(): Administrator {
    return this.currentUserInternal$.value;
  }

  public get isInternalAdmin(): boolean {
    return this.currentUserValue?.AdministratorType === AccountTypeEnum.Internal;
  }

  public get subscriptionUrl(): string {
    return this.authService.prepareSubscriptionUrl(this.currentUserValue.ProviderInfo?.Id);
  }

  public get brandingSetting$(): Observable<BrandingSetting> {
    return this.currentBrandingSettingInternal$.asObservable();
  }

  public get authTokenBySession$() {
    return this.authService.authTokenBySessionRequest$().pipe(
      tap((res) => this.oAuthCredentialInternal$.next(res)),
      share()
    );
  }

  public getRememberMeTime() {
    return this.authService.getRememberMeTime();
  }

  public checkAdminToken(): Observable<boolean> {
    return this.authService.checkAdminToken();
  }

  public getAgentLocalBaseUrl(): Observable<{ endpoint: string }> {
    return this.authService.getAgentLocalBaseUrl();
  }

  public applyRememberMe() {
    return this.authService.applyRememberMe().pipe(
      tap((tokens) => {
        this.credentials = {
          ...this.oAuthCredentialInternal$.value,
          access_token: tokens.access_token,
          refresh_token: tokens.refresh_token
        };
      })
    );
  }

  public get credentials$() {
    return this.oAuthCredentialInternal$;
  }

  // Setters part

  public set credentials(value: OAuthCredential) {
    this.oAuthCredentialInternal$.next(value);
  }

  public set oneTimeSessionCredentials(value: OAuthCredential) {
    this.oneTimeSession.credentials = value;
  }

  // Internal part

  private initialStreams(): void {
    this.oAuthCredentialInternal$.pipe(untilDestroyed(this)).subscribe((tokenData) => {
      if (!tokenData) {
        this.appPersistent.data.showAlertAboutBounceList = true;
        this.authService.removeTokenData();
      } else {
        this.authService.setTokenData(tokenData);
      }
    });

    this.appPersistent.change
      .pipe(
        filter((state) => this.currentUserInternal$.value && state.userId !== this.currentUserInternal$.value.Id),
        tap(() => !this.environment.production && this.clearSessionData()),
        switchMap(() => this.fetchUser$()),
        untilDestroyed(this)
      )
      .subscribe();
  }

  private setAuthError(error) {
    this.authError = error;
  }

  protected catchFetchUserError() {
    if (this.isOneTimeSession) {
      this.endOneTimeSession();

      return throwError(() => this.authError);
    }

    this.router.navigate([RoutingPath.Login], {
      queryParams: { logoff: 1 }
    });

    return of(null);
  }

  private fetchUser$(deletePermissions: string[] = []): Observable<Administrator> {
    this.meLoading$.next(true);
    return this.authService.fetchUserRequest$().pipe(
      catchError((error) => {
        this.setAuthError(error);
        return this.catchFetchUserError();
      }),
      map((res) => {
        const user: Administrator = Object.assign(new Administrator(), res);
        this.appPersistent.userId$.emit(user.Id);
        this.appPersistent.resetState();
        if (deletePermissions?.length) {
          deletePermissions.forEach((key) => this.abilityService.deletePermission(key));
        }
        user.Permissions && this.abilityService.set(PermissionsService.handlePermissions(user));
        // emit next user ONLY after the handlePermission operation
        // there are some places where permissions are updated based on some additional parameters
        // it shouldn't be updated after that here
        this.currentUserInternal$.next(user);
        res?.BrandingSetting && this.currentBrandingSettingInternal$.next(res.BrandingSetting);
        this.appPersistent.data.userId = user.Id;
        this.appPersistent.data.signUpDate = user.SignUpDate;

        try {
          // Add a user data for Sentry
          const ownerId = user.ProviderInfo?.Id;
          const scope = Sentry.getGlobalScope();
          scope.setUser({
            username: user.FirstName + user.LastName,
            email: user.Email,
            id: user.Id,
            ownerId
          });
          scope.setExtra('Provider', user?.IsProvider);
          scope.setExtra('Super Admin', user.IsSuperAdmin);
          scope.setExtra('Read Only', user.IsReadOnly);
          scope.setTag('user.ownerId', ownerId);
        } catch (err) {
          noop;
        }
        return user;
      }),
      finalize(() => {
        this.meLoaded$.next(true);
        this.meLoading$.next(false);
      }),
      share()
    );
  }

  // Login part

  public loginRequest$({ email, password }): Observable<boolean> {
    return this.authService.loginRequest$({ email, password });
  }

  // Token part

  public getOAuthIdentity(params: OAuthCodeParams): Observable<Administrator> {
    this.tokenLoading$.next(true);
    return this.authService.oauthTokenBySessionRequest$(params).pipe(
      tap((res) => this.oAuthCredentialInternal$.next(res)),
      switchMap(() => this.fetchCurrentUser$),
      finalize(() => this.tokenLoading$.next(false))
    );
  }

  public tryRefreshToken$(): Observable<boolean> {
    return this.oAuthCredentialInternal$.asObservable().pipe(
      take(1),
      switchMap((credentials) =>
        credentials
          ? this.refreshToken$().pipe(
              map(() => true),
              catchError(() => of(false))
            )
          : of(false)
      ),
      share()
    );
  }

  public refreshToken$(): Observable<OAuthCredential> {
    return this.authTokenBySession$;
  }

  public tempToken$(userId: string, hid = null, target = 'OneTime'): Observable<TempTokenData> {
    const data = { tokenTarget: hid ? target : 'TempInstance', computerHid: hid ?? null };
    return this.isMBSMode ? this.authService.tempTokenRequest$(userId, data) : this.onlineAccessService.tempTokenRequest$(hid, data);
  }

  public oneTimeAuthTokenRequest$(): Observable<OneTimeAuthToken> {
    return this.authService.oneTimeAuthTokenRequest$();
  }

  // Cleaning part

  public endOneTimeSession(): void {
    this.oneTimeSession.end();
  }

  public clearSessionData(): void {
    this.oAuthCredentialInternal$.next(null);

    this.abilityService.clear();

    this.currentUserInternal$.next(null);
    this.currentBrandingSettingInternal$.next(null);
    this.meLoaded$.next(false);
  }

  public fetchCurrentUserAndDeletePermissions$(permissions: string[]): Observable<Administrator> {
    return this.fetchUser$(permissions);
  }

  // Two Factor Authentication part

  public requestPermissions(payload: TwoFactorComputers): Observable<boolean> {
    return this.authService.permissionRequest$(payload).pipe(
      map((res) => {
        this.oAuthCredentialInternal$.next(Object.assign({}, this.oAuthCredentialInternal$.value, { access_token: res.access_token }));
        this.twoFAConfirmedInternal$.next(true);
        return true;
      })
    );
  }

  public getDropUserToken(): Observable<OAuthCredential> {
    return this.authService.getDropUserToken();
  }

  public cancelRequestPermission(): void {
    this.twoFAConfirmedInternal$.next(false);
  }

  // Apps part

  public getAppsToken(request: { domain: string }): Observable<{ access_token: string }> {
    return this.authService.appsTokenRequest$(request);
  }

  // Password part

  public checkPasswordStrength(password: string): Observable<PassCheckType> {
    return this.passwordService.checkPasswordStrength(password);
  }

  public getPasswordRules(): Observable<string[]> {
    return this.passwordService.getPasswordRules();
  }

  public generatePassword(): Observable<string> {
    return this.passwordService.generatePassword();
  }

  public setNewPassword(params: SetPasswordParams): Observable<null> {
    return this.passwordService.setNewPassword(params);
  }

  // Other

  public getFeedBackToken() {
    return this.authService.feedBackTokenRequest$();
  }

  public redirectToSupportPortal(): void {
    window.open(`${location.origin}${RoutingPath.SupportPortal}?force=true`, '_blank');
  }

  public changeEmail(value: { email: string; password: string }) {
    return this.authService.changeEmailRequest$(value);
  }

  public allowOfflineEdit(state = true): Observable<null> {
    return this.authService.allowOfflineEditRequest$(state).pipe(switchMap((state) => this.fetchUser$().pipe(map(() => state))));
  }

  public setCompleteGettingStartWizard(): void {
    this.currentUserInternal$.next({ ...this.currentUserInternal$.value, ...{ IsWizardComplete: true } });
  }

  public loginByCredentialsInternal(credentials: AuthCredentials) {
    return this.authService.loginByCredentialsInternal(credentials);
  }

  public loginByCredentials(credentials: AuthCredentials) {
    return this.authService.loginByCredentials(credentials);
  }

  public loginUserByCredentials(credentials: AuthCredentials, data: DataForLoginUser) {
    return this.authService.loginUserByCredentials(credentials, data);
  }

  public getTempTokenForAdmin(userId: string, computerHid: string) {
    return this.authService.tempTokenRequest$(userId, { tokenTarget: 'OneTime', computerHid });
  }

  public loginByGoogle(credentials: AuthGoogleCredentials) {
    return this.authService.loginByGoogle(credentials);
  }

  public loginUserByGoogle(credentials: AuthGoogleCredentials) {
    return this.authService.loginUserByGoogle(credentials);
  }

  public loginByMicrosoft(credentials: AuthMicrosoftCredentials) {
    return this.authService.loginByMicrosoft(credentials);
  }

  public loginUserByMicrosoft(credentials: AuthMicrosoftCredentials) {
    return this.authService.loginUserByMicrosoft(credentials);
  }

  public loginByAdminToken(token: string) {
    return this.authService.loginByAdminToken(token);
  }

  public loginByAuthId(credentials: AuthIdCredentials) {
    return this.authService.loginByAuthId(credentials);
  }

  public logout() {
    return this.authService.logout();
  }

  public getQrCode() {
    return this.authService.getQrCode();
  }

  public loginByQr(rememberMe: boolean) {
    return this.authService.loginByQr(rememberMe);
  }
}
